/**
 * SSR-safe motion runtime.
 *
 * All heavy libraries (GSAP, ScrollTrigger, Lenis) are dynamically imported the
 * first time they're needed — never during server render — so the SSR HTML and
 * LCP are untouched. Everything degrades gracefully when JS is unavailable or
 * the user prefers reduced motion.
 */

export type GsapMods = {
  gsap: typeof import("gsap").gsap;
  ScrollTrigger: typeof import("gsap/ScrollTrigger").ScrollTrigger;
};

let motionPromise: Promise<GsapMods | null> | null = null;

export function isBrowser(): boolean {
  return typeof window !== "undefined";
}

export function prefersReducedMotion(): boolean {
  return (
    isBrowser() &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function isTouch(): boolean {
  return (
    isBrowser() &&
    (window.matchMedia("(pointer: coarse)").matches || "ontouchstart" in window)
  );
}

/** Lazy-load GSAP + ScrollTrigger. Resolves null when motion is disabled. */
export function loadMotion(): Promise<GsapMods | null> {
  if (!isBrowser() || prefersReducedMotion()) return Promise.resolve(null);
  if (!motionPromise) {
    motionPromise = Promise.all([
      import("gsap"),
      import("gsap/ScrollTrigger"),
    ]).then(([gsapMod, stMod]) => {
      const gsap = gsapMod.gsap ?? gsapMod.default;
      const ScrollTrigger = stMod.ScrollTrigger ?? stMod.default;
      gsap.registerPlugin(ScrollTrigger);
      return { gsap, ScrollTrigger };
    });
  }
  return motionPromise;
}

/** Jump to the top of the page — used on route change. */
export function scrollToTop(_immediate = true): void {
  if (isBrowser()) {
    window.scrollTo(0, 0);
  }
}

/** Refresh ScrollTrigger measurements (after route/content changes). */
export function refreshScrollTriggers(): void {
  if (!isBrowser() || !motionPromise) return;
  motionPromise.then((mods) => mods?.ScrollTrigger.refresh());
}
